/**
 * Helpers for Shop the Look HotspotContent entries (image asset / Image entry, Video entry).
 */

import {normalizeVideoId} from '@amblique/king-living/app/utils/video-utils';

/**
 * Contentful often returns protocol-relative asset URLs (`//images.ctfassets.net/...`).
 *
 * @param {string} url
 * @returns {string}
 */
export function normalizeContentfulMediaUrl(url) {
  if (typeof url !== 'string' || !url) {
    return '';
  }

  const t = url.trim();

  if (t.startsWith('//')) {
    return `https:${t}`;
  }

  return t;
}

/**
 * Normalize Contentful image field (Asset, Image entry, or array of either).
 *
 * @param {unknown} imageField
 * @returns {string}
 */
export function resolveHotspotContentImageUrl(imageField) {
  if (imageField == null) {
    return '';
  }

  const raw = Array.isArray(imageField) ? imageField[0] : imageField;

  if (!raw || typeof raw !== 'object') {
    return '';
  }

  if (typeof raw.url === 'string' && raw.url) {
    return normalizeContentfulMediaUrl(raw.url);
  }

  const fileUrl = raw.file && typeof raw.file === 'object' ? raw.file.url : null;

  if (typeof fileUrl === 'string' && fileUrl) {
    return normalizeContentfulMediaUrl(fileUrl);
  }

  const desktop = raw.desktopAsset;
  const tablet = raw.tabletAsset;
  const mobile = raw.mobileAsset;

  const fromResponsive =
    (typeof desktop?.url === 'string' && desktop.url) ||
    (typeof tablet?.url === 'string' && tablet.url) ||
    (typeof mobile?.url === 'string' && mobile.url) ||
    '';

  return fromResponsive ? normalizeContentfulMediaUrl(fromResponsive) : '';
}

/**
 * @param {Record<string, unknown>|null|undefined} videoEntry
 * @returns {boolean}
 */
export function hotspotContentHasLinkedVideo(videoEntry) {
  if (!videoEntry || typeof videoEntry !== 'object') {
    return false;
  }

  const assetUrl = videoEntry.videoAsset?.url;
  const idOrUrl = videoEntry.videoIdOrUrl;

  return Boolean(
    (typeof assetUrl === 'string' && assetUrl.trim()) ||
    (typeof idOrUrl === 'string' && idOrUrl.trim())
  );
}

/**
 * Prefer direct file URL for native HTML5 video (muted autoplay).
 *
 * @param {Record<string, unknown>|null|undefined} videoEntry
 * @returns {string|null}
 */
export function getHotspotContentNativeVideoSrc(videoEntry) {
  if (!videoEntry || typeof videoEntry !== 'object') {
    return null;
  }

  const assetUrl = videoEntry.videoAsset?.url;
  const ct = String(videoEntry.videoAsset?.contentType || '').toLowerCase();

  if (typeof assetUrl === 'string' && assetUrl.trim()) {
    const u = normalizeContentfulMediaUrl(assetUrl.trim());

    if (ct.startsWith('video/') || /\.(mp4|webm|ogg|m4v)(\?|$)/i.test(u)) {
      return u;
    }
  }

  const idOrUrl = videoEntry.videoIdOrUrl;

  if (typeof idOrUrl === 'string' && idOrUrl.trim()) {
    const s = normalizeContentfulMediaUrl(idOrUrl.trim());

    if (/^https?:\/\//i.test(s) && /\.(mp4|webm|ogg|m4v)(\?|$)/i.test(s)) {
      return s;
    }
  }

  return null;
}

/**
 * Embed id/URL for the shared Video component (not used for direct MP4/WebM files).
 *
 * @param {Record<string, unknown>|null|undefined} videoEntry
 * @returns {string|null}
 */
export function getHotspotContentEmbedVideoId(videoEntry) {
  if (!videoEntry || typeof videoEntry !== 'object') {
    return null;
  }

  if (getHotspotContentNativeVideoSrc(videoEntry)) {
    return null;
  }

  if (!hotspotContentHasLinkedVideo(videoEntry)) {
    return null;
  }

  if (typeof videoEntry.videoIdOrUrl === 'string' && videoEntry.videoIdOrUrl.trim()) {
    return normalizeVideoId(videoEntry.videoIdOrUrl);
  }

  if (typeof videoEntry.videoAsset?.url === 'string' && videoEntry.videoAsset.url.trim()) {
    return videoEntry.videoAsset.url.trim();
  }

  return null;
}

/**
 * Desktop hotspot panel: main `image` + `video` only.
 *
 * @param {Record<string, unknown>|null|undefined} item
 * @returns {{ videoEntry: unknown, imageField: unknown }}
 */
export function resolveHotspotPanelDesktopMediaFields(item) {
  return {
    videoEntry: item?.video ?? null,
    imageField: item?.image ?? null,
  };
}

/**
 * Mobile: `mobileVideo` if set, else desktop video, else `mobileImage`, else desktop image.
 *
 * @param {Record<string, unknown>|null|undefined} item
 * @returns {{ videoEntry: unknown, imageField: unknown }}
 */
export function resolveHotspotPanelMobileMediaFields(item) {
  const mv = item?.mobileVideo;
  const mvOk = mv && (hotspotContentHasLinkedVideo(mv) || getHotspotContentNativeVideoSrc(mv));

  if (mvOk) {
    return {imageField: null, videoEntry: mv};
  }

  const dv = item?.video;
  const dvOk = dv && (hotspotContentHasLinkedVideo(dv) || getHotspotContentNativeVideoSrc(dv));

  if (dvOk) {
    return {imageField: null, videoEntry: dv};
  }

  const mi = item?.mobileImage;

  if (resolveHotspotContentImageUrl(mi)) {
    return {imageField: mi, videoEntry: null};
  }

  return {
    imageField: item?.image ?? null,
    videoEntry: null,
  };
}

/**
 * Compare resolved desktop vs mobile media (skip duplicate stacked layers).
 *
 * @param {unknown} videoEntry
 * @param {unknown} imageField
 * @returns {string}
 */
export function getHotspotPanelMediaSignature(videoEntry, imageField) {
  const native = getHotspotContentNativeVideoSrc(videoEntry);

  if (native) {
    return `n:${native}`;
  }

  if (videoEntry && typeof videoEntry === 'object' && hotspotContentHasLinkedVideo(videoEntry)) {
    let id = '';

    if (typeof videoEntry.videoIdOrUrl === 'string' && videoEntry.videoIdOrUrl.trim()) {
      id = normalizeVideoId(videoEntry.videoIdOrUrl);
    } else if (typeof videoEntry.videoAsset?.url === 'string' && videoEntry.videoAsset.url.trim()) {
      id = videoEntry.videoAsset.url.trim();
    }

    return `e:${id}`;
  }

  return `i:${resolveHotspotContentImageUrl(imageField)}`;
}

/**
 * @param {Record<string, unknown>|null|undefined} item
 * @returns {boolean}
 */
export function hotspotPanelMobileMediaDiffersFromDesktop(item) {
  const d = resolveHotspotPanelDesktopMediaFields(item);
  const m = resolveHotspotPanelMobileMediaFields(item);

  return (
    getHotspotPanelMediaSignature(d.videoEntry, d.imageField) !==
    getHotspotPanelMediaSignature(m.videoEntry, m.imageField)
  );
}
